import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Save, Mail, Send, AlertCircle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HUMAN_VARS = {
  welcome: "{{name}}, {{email}}, {{brand}}, {{app_url}}",
  password_reset: "{{name}}, {{brand}}, {{reset_url}}",
  subscription_pending_user: "{{name}}, {{plan_name}}, {{billing_period}}, {{price}}, {{payment_method}}, {{transfer_details}}, {{admin_email}}",
  subscription_pending_admin: "{{name}}, {{email}}, {{phone}}, {{plan_name}}, {{billing_period}}, {{price}}, {{payment_method}}, {{notes}}, {{app_url}}",
  subscription_approved: "{{name}}, {{plan_name}}, {{expires_on}}, {{app_url}}",
  subscription_overdue: "{{name}}, {{plan_name}}, {{expires_on}}, {{renew_url}}",
  marketing: "{{name}}, {{brand}}, {{message}}, {{app_url}}",
  support_request: "{{name}}, {{email}}, {{subject_line}}, {{message}}",
};

export default function AdminEmails() {
  const { getAdminAxios, admin } = useOutletContext();
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState("welcome");
  const [transferDetails, setTransferDetails] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [c, t, td] = await Promise.all([
        getAdminAxios().get(`${API}/admin/email-config-status`),
        getAdminAxios().get(`${API}/admin/email-templates`),
        getAdminAxios().get(`${API}/admin/transfer-details`),
      ]);
      setConfig(c.data);
      setTemplates(t.data);
      setTransferDetails(td.data?.details || "");
      if (t.data.length && !t.data.find((x) => x.key === tab)) setTab(t.data[0].key);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [getAdminAxios, tab]);

  useEffect(() => { refresh(); }, [refresh]);

  const current = templates.find((t) => t.key === tab);

  const updateField = (field, value) => {
    setTemplates((prev) => prev.map((t) => (t.key === tab ? { ...t, [field]: value } : t)));
  };

  const save = async () => {
    if (!current) return;
    setSaving(true); setError("");
    try {
      await getAdminAxios().put(`${API}/admin/email-templates/${current.key}`, {
        label: current.label, enabled: current.enabled,
        subject: current.subject, body_html: current.body_html,
      });
      setSavedAt(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!current) return;
    const to = window.prompt("¿A qué email mando la prueba?", admin?.email || "");
    if (!to) return;
    setTesting(true); setError("");
    try {
      const { data } = await getAdminAxios().post(`${API}/admin/email-templates/${current.key}/test`, {
        to, app_url: window.location.origin,
      });
      if (data.ok) {
        alert(`✅ Test enviado a ${to}. Revisá la bandeja de entrada (y spam).`);
      } else {
        alert("⚠️ No se pudo enviar. Revisá las credenciales SMTP en el .env.");
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setTesting(false); }
  };

  const saveTransferDetails = async () => {
    setSaving(true);
    try {
      await getAdminAxios().put(`${API}/admin/transfer-details`, { details: transferDetails });
      setSavedAt(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="p-8"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  }

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-emails-page">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2"><Mail className="w-6 h-6" /> Emails</h1>
          <p className="text-sm text-slate-400 mt-1">Editá lo que reciben tus clientes y configurá los datos de transferencia bancaria.</p>
        </div>
        <div className={`text-xs px-3 py-2 rounded border flex items-center gap-2 ${config?.configured ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-red-500/40 bg-red-500/10 text-red-300"}`}>
          {config?.configured ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
          {config?.configured ? `SMTP OK · ${config.from_email}` : "SMTP no configurado — agregá SMTP_USER y SMTP_PASSWORD al .env"}
        </div>
      </div>

      {error && <div className="mb-4 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">⚠️ {error}</div>}
      {savedAt && <div className="mb-4 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300">✓ Guardado a las {savedAt.toLocaleTimeString()}</div>}

      {/* Transfer details first — used in subscription emails */}
      <Card className="bg-[#13112b] border-indigo-900/30 mb-6">
        <CardHeader>
          <CardTitle className="text-white text-base">Datos para transferencia bancaria</CardTitle>
          <CardDescription className="text-slate-400">Esto aparece en el email que recibe el cliente cuando elige "Transferencia bancaria" como método de pago.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea rows={6} value={transferDetails} onChange={(e) => setTransferDetails(e.target.value)}
                    className="bg-black/40 border-white/10 text-white font-mono text-xs"
                    placeholder="Banco Galicia&#10;CBU 0070123 4500001234567890&#10;Alias: BO.TITO.PAGOS&#10;Titular: Tu Nombre — CUIT 20-12345678-1" data-testid="transfer-details" />
          <Button onClick={saveTransferDetails} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-transfer-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Guardar datos
          </Button>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/5 border border-white/10 flex-wrap h-auto">
          {templates.map((t) => (
            <TabsTrigger key={t.key} value={t.key} className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">
              {t.enabled === false && <span className="text-slate-600 mr-1">·</span>}
              {t.label.replace(/\s*\(.*?\)\s*/g, "").trim()}
            </TabsTrigger>
          ))}
        </TabsList>

        {templates.map((t) => (
          <TabsContent key={t.key} value={t.key}>
            <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
              <CardHeader>
                <CardTitle className="text-white text-base flex items-center justify-between">
                  <span>{t.label}</span>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-300">Habilitado</Label>
                    <Switch checked={t.enabled !== false}
                            onCheckedChange={(v) => setTemplates((p) => p.map(x => x.key === t.key ? { ...x, enabled: v } : x))}
                            className="data-[state=checked]:bg-emerald-500" />
                  </div>
                </CardTitle>
                <CardDescription className="text-slate-400 text-xs font-mono">
                  Variables disponibles: {HUMAN_VARS[t.key] || "—"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300 text-xs uppercase">Asunto</Label>
                  <Input value={current?.key === t.key ? current.subject || "" : t.subject || ""}
                         onChange={(e) => updateField("subject", e.target.value)}
                         className="bg-white/5 border-white/10 text-white" data-testid={`tpl-${t.key}-subject`} />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase">Cuerpo HTML</Label>
                  <Textarea rows={16} value={current?.key === t.key ? current.body_html || "" : t.body_html || ""}
                            onChange={(e) => updateField("body_html", e.target.value)}
                            className="bg-black/40 border-white/10 text-emerald-300 font-mono text-xs" data-testid={`tpl-${t.key}-body`} />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid={`tpl-${t.key}-save`}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Guardar
                  </Button>
                  <Button onClick={sendTest} disabled={testing || !config?.configured} variant="outline" data-testid={`tpl-${t.key}-test`}>
                    {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Mandar test
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
